// Global Instructions Rule Applied!
//
// Statements section of the statement run form (spec A3.14 stage 5).
//
// Generating a fund stores one Fund Financial Statements and one Capital
// Account Statement per investor (a consolidated fund: the consolidated
// statement and, while its entity statements are on, one per legal entity).
// This section is where they are reviewed: per statement its status, the
// stored PDF (View, Download) and Generate again, which replaces that one
// statement from the books as they are now, under the same version.
//
// A stored statement is a fixed document. A statement that failed says why
// on its row; "Generate failed" tries those again.
//
// Review (stage 5 part 3): the Issued column says when a statement was
// marked as issued (on its fund, in the Funds section). Generating an issued
// statement again produces its next version, tagged "Revised". "Out of date"
// marks a statement whose books or settings have changed since it was
// generated (asked per fund after the form has loaded: "Checking" until
// then). "Download all" hands back the listed statements as one archive.
//
// Sending (stage 6): "Send documents" shows what would go out - per account
// its documents of the document groups ticked (fund reports, statements)
// for the issued funds, their size, its recipients on the recipients topic -
// and creates a DRAFT email, which opens in the compose form to be reviewed
// and queued there. Nothing is sent from this section. While a draft
// statement email holds a document group, that group goes out from the
// draft (the button opens it; several drafts become a menu) and the groups
// no draft holds are still sent from here. The Sent column shows, per
// document, whether it went and in which email. The statement of a
// fund's carry row (the commitment that receives the carried interest and
// has nothing committed) is generated with the others and never sent: its
// row says "Not sent", and it is not in the "sent" count.
//
// Parts (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.3): an account whose
// documents do not fit one email is sent in several - "Create emails" makes
// one sibling draft per part, named "(1 of 3)" ... The first opens; the
// others are offered in a toast with a link each, and every part is in the
// "Open statement email" menu and in the Sent column of the documents it
// carries.
//
// The fund report (docs/QUARTERLY_REPORT_DISTRIBUTION_DESIGN.md d.1) is a
// row like the pack: 'Fund report', for all investors, with View, Download,
// Generate again and the Sent column. Its "Out of date" is about the Period
// Highlights set it carries.
//
// The capital call form's Notices section is the pattern.

import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, Dropdown, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { DownloadOutlined, MailOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { getSignedUrl } from '../../../core/services/filesService.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { saveBlob } from '../../document_reports/services/documentReportsService.ts'
import { downloadStatements, fetchStoredFile, storedFile } from '../utils/storedStatements.ts'
import type { RunFundChangesMap } from '../hooks/useRunFundChanges.ts'
import type { CreatedStatementCampaign } from '../services/investorStatementsService.ts'
import {
  InvestorStatementRow,
  InvestorStatementRun,
  RunFundState,
  StatementKind,
  StatementScope,
  StatementSendState,
  StatementStatusCode
} from '../../../state_manager/data_models/investor_statement_run.ts'
import type { RunDescriptionWarning } from '../../../state_manager/data_models/investor_statement_run.ts'
import { changedPartsText, DATE_FORMAT, fileSizeText, RUN_TABLE_SCROLL, runSummaryTags } from '../utils/runDisplay.ts'
import { DescriptionWarningTags } from './DescriptionWarningTags.tsx'
import SendStatementsModal, { draftedGroupCodes, sendableGroupCodes } from './SendStatementsModal.tsx'

const { Text } = Typography

const ALL_FUNDS = ''
const EMAIL_MODULE = 'email'
const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

type GenerateTarget = { runFundIds?: string[] | null; statementIds?: string[] | null }

interface RunStatementsSectionProps {
  run: InvestorStatementRun
  generate: (target?: GenerateTarget) => Promise<{ count: number | null; error?: string }>
  generating: boolean
  /** "Out of date", per fund of the run (useRunFundChanges). */
  changes: RunFundChangesMap
  /** The unreviewed or changed company short descriptions a stored fund
   *  report printed, per statement id (useRunDescriptionWarnings). */
  descriptionWarnings: Record<string, RunDescriptionWarning[]>
  /** Re-read the run (after a statement email was created). */
  refetch?: () => Promise<void>
}

const isPending = (row: InvestorStatementRow) =>
  row.investor_statement_statuses__code === StatementStatusCode.pending && !row.prog__is_stalled

const isFailed = (row: InvestorStatementRow) => row.investor_statement_statuses__code === StatementStatusCode.failed

/** What a row is a statement of: 'Financial statements', 'Capital account',
 *  or for an account in one legal entity 'Capital account · Origin SBIC'. */
export const statementTitle = (row: InvestorStatementRow): string =>
  row.prog__scope === StatementScope.pocket
    ? `${row.investor_statement_types__short_label} · ${row.prog__entity_name}`
    : row.prog__scope === StatementScope.subscription
      ? `${row.investor_statement_types__short_label} · Consolidated`
      : row.investor_statement_types__short_label

export default function RunStatementsSection({
  run,
  generate,
  generating,
  changes,
  descriptionWarnings,
  refetch
}: RunStatementsSectionProps) {
  const dispatch = useDispatch()
  const { openFormInTab } = useTabManagerContext()
  const [fundFilter, setFundFilter] = useState<string>(ALL_FUNDS)
  const [busyRow, setBusyRow] = useState<string | null>(null)
  const [busyFailed, setBusyFailed] = useState(false)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [sendOpen, setSendOpen] = useState(false)

  const openEmail = useCallback(
    (id: string, name?: string | null) => openFormInTab(EMAIL_MODULE, id, name ?? null),
    [openFormInTab]
  )
  const handleEmailCreated = useCallback(
    (campaign: CreatedStatementCampaign) => {
      setSendOpen(false)
      openEmail(campaign.email_campaigns__id, campaign.email_campaigns__name)
      // the other parts of the set: a link each, so none is lost from view
      const others = campaign.campaigns.filter((c) => c.email_campaigns__id !== campaign.email_campaigns__id)
      if (others.length > 0) {
        toast.info(
          <span>
            Also drafted:{' '}
            {others.map((c, i) => (
              <React.Fragment key={c.email_campaigns__id}>
                {i > 0 ? ', ' : ''}
                <button
                  type='button'
                  className={RECORD_LINK_CLASS}
                  onClick={() => openEmail(c.email_campaigns__id, c.email_campaigns__name)}
                >
                  {c.email_campaigns__name}
                </button>
              </React.Fragment>
            ))}
          </span>,
          { autoClose: false }
        )
      }
      void refetch?.()
    },
    [openEmail, refetch]
  )

  const fundOptions = useMemo(
    () => [
      { value: ALL_FUNDS, label: 'All funds' },
      ...run.funds.map((f) => ({ value: f.investor_statement_run_funds__id, label: f.prog__name }))
    ],
    [run.funds]
  )
  // a fund removed from the run while it was picked: back to all funds
  const activeFilter = fundOptions.some((o) => o.value === fundFilter) ? fundFilter : ALL_FUNDS

  // a fund that is queued or generating takes no second request
  const busyFunds = useMemo(
    () =>
      new Set(
        run.funds
          .filter((f) => f.prog__state === RunFundState.queued || f.prog__state === RunFundState.generating)
          .map((f) => f.investor_statement_run_funds__id)
      ),
    [run.funds]
  )

  const rows = useMemo(
    () =>
      activeFilter === ALL_FUNDS
        ? run.statements
        : run.statements.filter((s) => s.investor_statement_run_funds__id === activeFilter),
    [run.statements, activeFilter]
  )
  const failed = useMemo(() => rows.filter(isFailed), [rows])

  /** The stored PDF, in a file-viewer tab (the house way to open a file). */
  const handleView = useCallback(
    async (row: InvestorStatementRow) => {
      const file = storedFile(row)
      if (!file) return
      try {
        const signed = await getSignedUrl(file.path, getAccessToken(store.getState().auth))
        const tabKey = `file-viewer-${file.path.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
        dispatch(
          addTab({
            id: tabKey,
            key: tabKey,
            title: file.name,
            path: `/file-viewer/${tabKey}`,
            type: 'file-viewer',
            module: 'file-viewer',
            closeable: true,
            fileUrl: signed.signed_url,
            fileName: file.name
          })
        )
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to open the statement')
      }
    },
    [dispatch]
  )

  const handleDownload = useCallback(async (row: InvestorStatementRow) => {
    const file = storedFile(row)
    if (!file) return
    setBusyRow(row.investor_statements__id)
    try {
      saveBlob(await fetchStoredFile(file.path), file.name)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : `Failed to download ${file.name}`)
    } finally {
      setBusyRow(null)
    }
  }, [])

  /** Replace the given statements: one request per fund they belong to
   *  would queue the funds one by one, so they go in one. */
  const regenerate = useCallback(
    async (targets: InvestorStatementRow[]): Promise<boolean> => {
      const res = await generate({
        runFundIds: Array.from(new Set(targets.map((t) => t.investor_statement_run_funds__id))),
        statementIds: targets.map((t) => t.investor_statements__id)
      })
      if (res.error || res.count == null) {
        showErrorToast(res.error || 'Failed to generate the statements')
        return false
      }
      toast.success(`Generating ${targets.length} statement${targets.length === 1 ? '' : 's'} again`)
      return true
    },
    [generate]
  )

  const handleRegenerateOne = useCallback(
    async (row: InvestorStatementRow) => {
      setBusyRow(row.investor_statements__id)
      try {
        await regenerate([row])
      } finally {
        setBusyRow(null)
      }
    },
    [regenerate]
  )

  const handleRegenerateFailed = useCallback(async () => {
    setBusyFailed(true)
    try {
      await regenerate(failed)
    } finally {
      setBusyFailed(false)
    }
  }, [regenerate, failed])

  const downloadable = useMemo(() => rows.filter((row) => storedFile(row)), [rows])
  const staleCount = useMemo(
    () =>
      rows.filter((row) => changes[row.investor_statement_run_funds__id]?.stale[row.investor_statements__id]).length,
    [rows, changes]
  )

  const handleDownloadAll = useCallback(async () => {
    setDownloadingAll(true)
    try {
      const fund = run.funds.find((f) => f.investor_statement_run_funds__id === activeFilter)
      const res = await downloadStatements(
        downloadable,
        `Statements - ${fund ? fund.prog__name : run.run.investor_statement_runs__name} - ${run.run.prog__period_label}`
      )
      if (res.saved === 0) showErrorToast('None of the statements could be downloaded')
      else if (res.failed.length > 0) showErrorToast(`Not in the download: ${res.failed.join(', ')}`)
    } finally {
      setDownloadingAll(false)
    }
  }, [downloadable, run.funds, run.run, activeFilter])

  const statusCell = useCallback(
    (row: InvestorStatementRow) => {
      const code = row.investor_statement_statuses__code
      const change = changes[row.investor_statement_run_funds__id]
      const staleParts = change?.stale[row.investor_statements__id]
      return (
        <div>
          <Space size={4} wrap>
            {row.prog__is_stalled ? (
              <Tooltip title='Generation did not finish: the server stopped while this statement was being produced. Generate it again.'>
                <Tag color='gold'>Stalled</Tag>
              </Tooltip>
            ) : (
              <Tag color={row.investor_statement_statuses__tag_color ?? undefined}>
                {row.investor_statement_statuses__label ?? code ?? '—'}
              </Tag>
            )}
            {row.prog__scope !== StatementScope.entity && !row.investor_statements__states_carry ? (
              <Tooltip title='The capital account at fair market value is stated before carried interest.'>
                <Tag>Before carry</Tag>
              </Tooltip>
            ) : null}
            {row.investor_statements__version > 1 ? (
              <Tooltip title='This statement replaces one that was issued: it prints "Revised" and the day it was generated.'>
                <Tag color='blue'>Revised · v{row.investor_statements__version}</Tag>
              </Tooltip>
            ) : null}
            {code !== StatementStatusCode.generated ? null : staleParts ? (
              <Tooltip title={`${changedPartsText(staleParts)} Generate it again to replace it.`}>
                <Tag color='gold'>Out of date</Tag>
              </Tooltip>
            ) : change?.status === 'checking' ? (
              <Tooltip title='Comparing the stored statement with the books and the settings as they are now.'>
                <Tag>Checking</Tag>
              </Tooltip>
            ) : null}
            {code === StatementStatusCode.generated ? (
              <DescriptionWarningTags items={descriptionWarnings[row.investor_statements__id]} />
            ) : null}
          </Space>
          {code === StatementStatusCode.failed && row.investor_statements__error_message ? (
            <div className='text-xs text-red-600 mt-1'>{row.investor_statements__error_message}</div>
          ) : null}
        </div>
      )
    },
    [changes, descriptionWarnings]
  )

  const sentCell = useCallback(
    (row: InvestorStatementRow) => {
      const state = row.prog__send_state
      // the carry row's statement is in no email (a state on it can only be
      // from before the row became the carry row: that is shown as it is)
      if (row.prog__is_carry_row && !state) {
        return (
          <Tooltip title='This commitment receives the carried interest and has nothing committed. Its statement is generated for the record and is not sent.'>
            <Tag>Not sent</Tag>
          </Tooltip>
        )
      }
      if (!state) return <Text type='secondary'>—</Text>
      const tag =
        state === StatementSendState.sent ? (
          <Tag color='green'>Sent{row.prog__sent_at ? ` ${dayjs(row.prog__sent_at).format(DATE_FORMAT)}` : ''}</Tag>
        ) : state === StatementSendState.failed ? (
          <Tooltip title="The email finished without this investor's message going out. Open the email to see why.">
            <Tag color='red'>Send failed</Tag>
          </Tooltip>
        ) : state === StatementSendState.draft ? (
          <Tag color='blue'>In draft email</Tag>
        ) : (
          <Tag color='blue'>Sending</Tag>
        )
      const campaignId = row.email_campaigns__id
      return (
        <div>
          {tag}
          {campaignId ? (
            <div>
              <button
                type='button'
                className={`${RECORD_LINK_CLASS} text-xs`}
                onClick={() => openEmail(campaignId, row.email_campaigns__name)}
              >
                {row.email_campaigns__name || 'Statement email'}
              </button>
            </div>
          ) : null}
        </div>
      )
    },
    [openEmail]
  )

  const columns = useMemo(
    (): ColumnsType<InvestorStatementRow> => [
      { title: 'Fund', key: 'fund', width: 160, render: (_: unknown, row) => row.prog__run_fund_name },
      { title: 'Statement', key: 'statement', width: 170, render: (_: unknown, row) => statementTitle(row) },
      {
        title: 'Investor',
        key: 'investor',
        width: 160,
        render: (_: unknown, row) => row.investors__name ?? <Text type='secondary'>All investors</Text>
      },
      { title: 'Status', key: 'status', render: (_: unknown, row) => statusCell(row) },
      {
        title: 'Pages',
        key: 'pages',
        width: 70,
        align: 'right',
        render: (_: unknown, row) => row.investor_statements__page_count ?? '—'
      },
      {
        title: 'Size',
        key: 'size',
        width: 90,
        align: 'right',
        render: (_: unknown, row) => fileSizeText(row.file_urls_investor_statements__file_size_bytes)
      },
      {
        title: 'Generated',
        key: 'generated',
        width: 110,
        render: (_: unknown, row) =>
          row.investor_statements__generated_at ? dayjs(row.investor_statements__generated_at).format(DATE_FORMAT) : '—'
      },
      {
        title: 'Issued',
        key: 'issued',
        width: 110,
        render: (_: unknown, row) =>
          row.investor_statements__issued_at ? dayjs(row.investor_statements__issued_at).format(DATE_FORMAT) : '—'
      },
      { title: 'Sent', key: 'sent', width: 140, render: (_: unknown, row) => sentCell(row) },
      {
        title: '',
        key: 'actions',
        width: 270,
        align: 'right',
        render: (_: unknown, row) => {
          const file = storedFile(row)
          const busy = busyRow === row.investor_statements__id
          const fundBusy = busyFunds.has(row.investor_statement_run_funds__id)
          const issued = !!row.investor_statements__issued_at
          const again = (
            <Button
              size='small'
              loading={busy && generating}
              disabled={fundBusy || (generating && !busy)}
              onClick={issued ? undefined : () => handleRegenerateOne(row)}
            >
              Generate again
            </Button>
          )
          return (
            <Space size={4}>
              {file ? (
                <Button size='small' onClick={() => handleView(row)}>
                  View
                </Button>
              ) : null}
              {file ? (
                <Button size='small' loading={busy && !generating} onClick={() => handleDownload(row)}>
                  Download
                </Button>
              ) : null}
              {isPending(row) ? null : (
                <Tooltip title={fundBusy ? 'The statements of this fund are being generated.' : undefined}>
                  <span>
                    {issued ? (
                      <Popconfirm
                        title='Generate this issued statement again?'
                        description={
                          <div className='max-w-sm'>
                            It was issued: the new one is version {row.investor_statements__version + 1}, printed
                            &quot;Revised&quot; with today&apos;s date. The issued file is kept.
                          </div>
                        }
                        okText='Generate again'
                        disabled={fundBusy || generating}
                        onConfirm={() => handleRegenerateOne(row)}
                      >
                        {again}
                      </Popconfirm>
                    ) : (
                      again
                    )}
                  </span>
                </Tooltip>
              )}
            </Space>
          )
        }
      }
    ],
    [statusCell, sentCell, busyRow, busyFunds, generating, handleView, handleDownload, handleRegenerateOne]
  )

  const failedBusy = failed.some((row) => busyFunds.has(row.investor_statement_run_funds__id))

  // a draft is the working copy of its document groups: the button opens
  // it. The groups no draft holds are still sent from here - a reports
  // email in a draft does not hold the statements. Before document groups
  // (none configured) one draft is the whole run's.
  const draftEmails = run.campaigns.filter((c) => c.email_campaigns__status === 'draft')
  const drafted = draftedGroupCodes(run)
  const undraftedGroups = run.run.prog__document_groups.filter(
    (g) => !drafted.includes(g.statement_document_groups__code)
  )
  const canSendMore = draftEmails.length === 0 || undraftedGroups.length > 0
  const sendBlockedReason =
    run.run.prog__send_blocked_reason ??
    (draftEmails.length > 0 && !sendableGroupCodes(run).some((code) => !drafted.includes(code))
      ? 'Everything issued so far is in the draft email already.'
      : undefined)
  // the run holds more than statements: the fund reports of some fund
  const hasReports = run.statements.some((row) => row.prog__kind === StatementKind.report)
  const sentCount = rows.filter(
    (row) => row.prog__scope !== StatementScope.entity && row.prog__send_state === StatementSendState.sent
  ).length
  // the statements that go to investors: not the pack, not the carry row's
  const investorRows = rows.filter(
    (row) => row.prog__scope !== StatementScope.entity && !row.prog__is_carry_row
  ).length

  const headerActions = (
    <>
      {runSummaryTags(run).map((tag) => (
        <Tag key={tag.key} color={tag.color}>
          {tag.text}
        </Tag>
      ))}
      {staleCount > 0 ? <Tag color='gold'>{staleCount} out of date</Tag> : null}
      {sentCount > 0 ? (
        <Tag color='green'>
          {sentCount} of {investorRows} sent
        </Tag>
      ) : null}
      {failed.length > 0 ? (
        <Button
          size='small'
          loading={busyFailed}
          disabled={generating || failedBusy}
          onClick={() => void handleRegenerateFailed()}
        >
          Generate failed
        </Button>
      ) : null}
      {downloadable.length > 0 ? (
        <Button
          size='small'
          icon={<DownloadOutlined />}
          loading={downloadingAll}
          onClick={() => void handleDownloadAll()}
        >
          Download all
        </Button>
      ) : null}
      {draftEmails.length === 1 ? (
        <Tooltip title='This run has a draft statement email. Review it and queue it from the email form.'>
          <Button
            size='small'
            icon={<MailOutlined />}
            onClick={() => openEmail(draftEmails[0].email_campaigns__id, draftEmails[0].email_campaigns__name)}
          >
            Open statement email
          </Button>
        </Tooltip>
      ) : draftEmails.length > 1 ? (
        <Dropdown
          menu={{
            items: draftEmails.map((c) => ({
              key: c.email_campaigns__id,
              label: c.email_campaigns__name || 'Statement email',
              onClick: () => openEmail(c.email_campaigns__id, c.email_campaigns__name)
            }))
          }}
        >
          <Button size='small' icon={<MailOutlined />}>
            Open statement email
          </Button>
        </Dropdown>
      ) : null}
      {canSendMore && run.statements.length > 0 ? (
        <Tooltip title={sendBlockedReason}>
          <span>
            <Button
              size='small'
              icon={<MailOutlined />}
              disabled={!!sendBlockedReason || generating || run.prog__is_active}
              onClick={() => setSendOpen(true)}
            >
              {hasReports ? 'Send documents' : 'Send statements'}
            </Button>
          </span>
        </Tooltip>
      ) : null}
    </>
  )

  return (
    <>
      <FormSection
        title='Statements'
        titleColor='green'
        scrollable={false}
        titleSuffix={
          run.funds.length > 1 ? (
            <Select
              size='small'
              style={{ minWidth: 220 }}
              value={activeFilter}
              options={fundOptions}
              aria-label='Fund'
              onChange={setFundFilter}
            />
          ) : null
        }
        headerActions={headerActions}
      >
        {rows.length === 0 ? (
          <Text type='secondary'>
            Nothing is stored yet. Generate a fund in the Funds section: its financial statements and a capital account
            statement per investor - and its fund report, where the fund includes one - are printed and stored here.
          </Text>
        ) : (
          <Table<InvestorStatementRow>
            size='small'
            pagination={false}
            rowKey='investor_statements__id'
            columns={columns}
            dataSource={rows}
            scroll={RUN_TABLE_SCROLL}
            tableLayout='auto'
          />
        )}
      </FormSection>
      <SendStatementsModal
        run={run}
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        onCreated={handleEmailCreated}
        changes={changes}
      />
    </>
  )
}
